"use client";

import { ArrowUpRightIcon, CheckIcon, CopyIcon } from "lucide-react";
import Link from "next/link";
import { useState, type ComponentProps, type CSSProperties, type MouseEvent, type ReactNode } from "react";

import { DISC_LOGO_CLASS, SOURCE_DISC_CLASS } from "@/components/app/runs/run-sources-used-view";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { BrandIcon } from "./icons/source-icons";
import type { EntityPopoverState } from "./use-entity-popover";

/*
 * One grammar for everything a message can point at, inside the workspace or
 * out: a chip that names the thing behind the same round source disc as an
 * answer's "Retrieved with" line, and a card about the thing itself. The card
 * leads with the entity (its mark, name, kind and home), shows what is known
 * about it, and says in its footer where that knowledge comes from.
 */

type DiscSize = "chip" | "card";

/** The round disc every source, person and place sits in. */
export function EntityDisc({ size, className, style, children }: { size: DiscSize; className?: string; style?: CSSProperties; children: ReactNode }) {
  return (
    <span
      aria-hidden
      data-slot="entity-disc"
      style={style}
      className={cn(SOURCE_DISC_CLASS, "ring-0", size === "chip" ? "size-4" : "size-10 border-border/80", className)}
    >
      {children}
    </span>
  );
}

/**
 * A product's own official mark inside a disc, drawn at app-icon scale: every
 * product the same way, so a row of them reads as one set.
 */
export function BrandDiscMark({ icon }: { icon: BrandIcon }) {
  return (
    // Bundled product marks only, never a provider-supplied image URL.
    // eslint-disable-next-line @next/next/no-img-element
    <img src={icon.src} alt="" className={cn(DISC_LOGO_CLASS, icon.monochrome && "dark:invert")} />
  );
}

// A reference in running text reads as text: its mark and a link-coloured
// name, no border or plate; a soft ground on hover says it can be opened.
const CHIP_CLASS =
  "not-typeset inline-flex min-w-0 max-w-full items-center gap-1 rounded-sm px-0.5 align-middle font-medium leading-5 text-link no-underline transition-colors hover:bg-muted/60 hover:text-link-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring aria-expanded:bg-muted/60";

type ChipLink = { href: string; external?: boolean };

/**
 * The inline chip: disc, name, and where it lives in a quieter voice. The
 * name fades in when a live preview renames it.
 */
export function EntityChip({
  link,
  disc,
  title,
  detail,
  popover,
  className,
  label,
  onClick,
  dataWorkspaceLink,
  discTone,
}: {
  link: ChipLink;
  disc: ReactNode;
  title: string;
  detail?: string | null;
  popover?: EntityPopoverState;
  className?: string;
  /** Accessible name when the visible words are not enough. */
  label?: string;
  /** Runs after the chip's own handling unless that prevented the default. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** Marks workspace links for readers that collect them, such as a record's siblings. */
  dataWorkspaceLink?: string;
  /** A workspace kind's colour, filling the disc as the composer's source discs are; its icon is drawn white. */
  discTone?: string | null;
}) {
  const content = (
    <>
      {/* A product's mark stands on its own at full size; a workspace kind keeps
          its filled colour disc; any other glyph reads at text size. */}
      <EntityDisc
        size="chip"
        className={cn("border-transparent bg-transparent [&>img]:size-full", !discTone && "[&>svg]:size-3.5")}
        style={discTone ? { backgroundColor: discTone } : undefined}
      >
        {disc}
      </EntityDisc>
      <span key={title} className="min-w-0 max-w-[30ch] truncate animate-in fade-in-0 duration-300 motion-reduce:animate-none">
        {title}
      </span>
      {detail ? <span className="min-w-0 max-w-[18ch] truncate text-xs font-normal text-muted-foreground">{detail}</span> : null}
    </>
  );
  const chipProps = popover?.chipProps;
  const props = {
    ...(chipProps ?? {}),
    className: cn(CHIP_CLASS, className),
    "aria-label": label,
    title: link.external ? link.href : undefined,
    "data-workspace-link": dataWorkspaceLink,
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      chipProps?.onClick(event);
      if (!event.defaultPrevented) onClick?.(event);
    },
  };
  return link.external ? (
    <a {...props} href={link.href} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    <Link {...props} href={link.href} prefetch={false}>
      {content}
    </Link>
  );
}

/** The card's frame, anchored to its chip. Its contents mount only while it is open. */
export function EntityPopover({
  popover,
  label,
  className,
  children,
}: {
  popover: EntityPopoverState;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Popover open={popover.open} onOpenChange={popover.onOpenChange}>
      <PopoverContent
        {...popover.popupProps}
        anchor={popover.anchor}
        initialFocus={false}
        finalFocus={false}
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={16}
        aria-label={label}
        className={cn(
          "not-typeset w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl p-0 text-sm shadow-lg",
          className,
        )}
      >
        {popover.open ? children : null}
      </PopoverContent>
    </Popover>
  );
}

/** The entity itself: its mark, its name, and what and where it is. */
export function EntityCardHeader({
  disc,
  title,
  subtitle,
  aside,
}: {
  disc: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 px-3.5 pt-3.5">
      <EntityDisc size="card">{disc}</EntityDisc>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="line-clamp-2 break-words font-semibold leading-snug text-foreground">{title}</p>
        {subtitle ? <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>
      {aside}
    </div>
  );
}

export function EntityCardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("space-y-3 px-3.5 pt-3", className)}>{children}</div>;
}

/** Facts as short lines, each led by an icon or a small disc. */
type EntityFact = { key: string; icon: ReactNode; text: ReactNode };

export function EntityCardFacts({ facts }: { facts: Array<EntityFact | null | undefined | false | "" | 0> }) {
  const shown = facts.filter((fact): fact is EntityFact => Boolean(fact));
  if (!shown.length) return null;
  return (
    <ul className="space-y-1.5 text-xs text-muted-foreground">
      {shown.map((fact) => (
        <li key={fact.key} className="flex min-w-0 items-center gap-2">
          <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-3.5">{fact.icon}</span>
          <span className="min-w-0 truncate">{fact.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** A state the entity is in for this viewer, with the one step that changes it. */
export function EntityCardNotice({ icon, children, action }: { icon?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div role="status" className="flex items-center gap-2.5 rounded-lg border border-dashed border-border px-3 py-2.5">
      {icon ? <span className="flex shrink-0 items-center [&_svg]:size-4 [&_svg]:text-muted-foreground">{icon}</span> : null}
      <p className="min-w-0 flex-1 text-xs leading-relaxed text-muted-foreground">{children}</p>
      {action}
    </div>
  );
}

/** Where the card's knowledge comes from, and what can be done with the entity. */
export function EntityCardFooter({ source, children }: { source: ReactNode; children?: ReactNode }) {
  return (
    <div className="mt-3.5 flex min-h-11 items-center gap-2 border-t border-border/70 bg-muted/35 px-3.5 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground">{source}</div>
      {children ? <div className="flex shrink-0 items-center gap-1">{children}</div> : null}
    </div>
  );
}

export function EntityCardSource({ disc, children }: { disc: ReactNode; children: ReactNode }) {
  return (
    <>
      <EntityDisc size="chip">{disc}</EntityDisc>
      <span className="min-w-0 truncate">{children}</span>
    </>
  );
}

export function EntityCardSkeleton({ lines = 2 }: { lines?: number }) {
  return (
    <div role="status" aria-label="Loading" className="space-y-2">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn("h-3", index === lines - 1 ? "w-2/3" : "w-full")} />
      ))}
    </div>
  );
}

export function OpenAction({
  href,
  external,
  label = "Open",
  onOpen,
}: {
  href: string;
  external?: boolean;
  label?: string;
  onOpen?: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const className = buttonVariants({ variant: "outline", size: "xs" });
  const icon = <ArrowUpRightIcon data-icon="inline-end" />;
  return external ? (
    <a className={className} href={href} target="_blank" rel="noopener noreferrer" onClick={onOpen}>
      {label}
      {icon}
    </a>
  ) : (
    <Link className={className} href={href} prefetch={false} onClick={onOpen}>
      {label}
      {icon}
    </Link>
  );
}

export function CopyLinkAction({ href }: { href: string }) {
  const [state, setState] = useState<"idle" | "copied" | "error">("idle");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(new URL(href, window.location.origin).href);
      setState("copied");
      window.setTimeout(() => setState("idle"), 1600);
    } catch {
      setState("error");
    }
  };
  return (
    <Button
      type="button"
      size="icon-xs"
      variant="ghost"
      aria-label={state === "copied" ? "Link copied" : state === "error" ? "Could not copy the link" : "Copy link"}
      title={state === "error" ? "Could not copy the link" : "Copy link"}
      onClick={() => void copy()}
    >
      {state === "copied" ? <CheckIcon /> : <CopyIcon />}
    </Button>
  );
}

export function CardAction(props: ComponentProps<typeof Button>) {
  return <Button type="button" size="xs" variant="ghost" {...props} />;
}

/** A thing that is not a link (a person, the Twin, an API key): hover, focus or a click opens its card. */
export function EntityHoverTarget({
  popover,
  label,
  className,
  children,
}: {
  popover: EntityPopoverState;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const { onKeyDown, ...chipProps } = popover.chipProps;
  return (
    <EntityHoverTargetView
      label={label}
      {...chipProps}
      className={className}
      onKeyDown={(event) => {
        onKeyDown(event);
        if (!popover.open && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          popover.onOpenChange(true, { reason: "trigger-press" });
        }
      }}
    >
      {children}
    </EntityHoverTargetView>
  );
}

/** The element an entity's card opens from, as markup; EntityHoverTarget adds the popover's props and handlers. */
export function EntityHoverTargetView({
  label,
  className,
  children,
  ...targetProps
}: {
  label: string;
  className?: string;
  children: ReactNode;
} & Omit<ComponentProps<"span">, "className" | "children">) {
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      {...targetProps}
      className={cn("cursor-pointer rounded-sm focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring", className)}
    >
      {children}
    </span>
  );
}
